import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { PopoverController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Activity, ActivityType } from '@omni/classes/activity/activity.class';
import { AppointmentActivity } from '@omni/classes/activity/appointment.activity.class';
import { SetBookingStatus } from '@omni/classes/activity/set-booking.activity.class';
import { TimeOffActivity } from '@omni/classes/activity/timeoff.class';
import { EntityNameForTranslation, LocalizationService } from '@omni/services/localization/localization.service';
import { DateTimeFormatsService } from '@omni/services/date-time-formats/date-time-formats.service';
import { GlobalUtilityService } from '@omni/services/global-utility.service';
import { ActivityService } from '@omni/services/activity/activity.service';

export type ActivityChipPopoverResult = 'open' | 'close';

@Component({
  selector: 'activity-chip-popover',
  templateUrl: './activity-chip-popover.component.html',
  styleUrl: './activity-chip-popover.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivityChipPopoverComponent {
  @Input() activity: Activity | undefined;
  /** Resolved chip fill. Passed explicitly because it's set on the calendar event
   *  mapping, not on the Activity instance — so activity.color isn't reliable for all
   *  types. Also the avatar's background, exactly as before. */
  @Input() chipColor: string = '';
  /** Ink for what sits ON the chip colour: the avatar glyph and the type-pill text.
   *  Derived against the fill, so for a dark chip this is deliberately light. */
  @Input() chipInk: string = '';
  /** Ink for what sits on this card's white surface: the title and the top banner.
   *  `chipInk` is wrong there by construction — on a dark chip it is light, which reads
   *  on the chip and disappears on the card. */
  @Input() chipInkOnCard: string = '';

  constructor(
    private readonly popoverCtrl: PopoverController,
    private readonly translate: TranslateService,
    private readonly localisationService: LocalizationService,
    private readonly dateTimeFormatsService: DateTimeFormatsService,
    private readonly globalUtilityService: GlobalUtilityService,
    private readonly activityService: ActivityService,
  ) {}

  get accentColor(): string {
    return this.chipColor || this.activity?.color || 'var(--ion-color-primary)';
  }

  /**
   * Location + Format rows are hidden for time-off, procedure-log and active kit-booking
   * chips — neither field carries meaningful info for those types.
   *
   * A CANCELLED kit booking shows both rows. That is inherited behaviour rather than a
   * product decision (see the doc), preserved deliberately.
   */
  get showLocationAndType(): boolean {
    const type = this.activity?.type;

    if (type === ActivityType.TimeOff || type === ActivityType.TimeOffRequest) return false;
    if (type === ActivityType.SurgeryOrder) return false;
    if (type === ActivityType.SetBooking) return this.isCancelledKitBooking;

    return true;
  }

  private get isCancelledKitBooking(): boolean {
    const status = (this.activity as any)?.rawData?.indskr_status;
    return status == SetBookingStatus.CANCELLED
      || status == SetBookingStatus.CANCELERROR
      || status == SetBookingStatus.PENDINGCANCEL;
  }

  get title(): string {
    return this.activity?.subject || '';
  }

  /** Meeting/call type label — mirrors the "Call Type" (FORMAT) field in the
   *  activities details pane, which reads activity.activityTypeName and runs
   *  it through LocalizationService so the label matches the user's language.
   *  Remote-detailing meetings short-circuit to REMOTE_MEETING. Returns N/A
   *  when nothing usable is available so the row still renders. */
  get typeLabel(): string {
    if (this.activity?.type === ActivityType.Appointment) {
      const appt = this.activity as AppointmentActivity;
      if (appt.isRemoteDetailing) return this.translate.instant('REMOTE_MEETING');
      return this.localisationService.getTranslatedString(appt.activityTypeName, EntityNameForTranslation.ActivityType)
        || this.translate.instant('N_A');
    }
    return this.activity?.indskr_typeFormattedValue
      || this.activity?.indskr_subtypeFormattedValue
      || this.activity?.activityTypeName
      || this.translate.instant('N_A');
  }

  /** Location field value, or N/A. Some flows persist the literal "No Location"
   *  string instead of leaving the field blank — treat that as missing. */
  get location(): string {
    const raw = this.activity?.location;
    const noLocation = this.translate.instant('NO_LOCATION');
    if (!raw || raw === noLocation || raw.toLowerCase() === 'no location') {
      return this.translate.instant('N_A');
    }
    return raw;
  }

  get isAppointment(): boolean {
    return this.activity?.type === ActivityType.Appointment;
  }

  get isTimeOff(): boolean {
    return this.activity?.type === ActivityType.TimeOff
      || this.activity?.type === ActivityType.TimeOffRequest;
  }

  /** Customer field label — the configurable "Customer/Contact" term, same as
   *  the contact field label in the activities details pane (globalCustomersText). */
  get customerLabel(): string {
    return this.globalUtilityService.globalCustomersText || this.translate.instant('CUSTOMER');
  }

  /** Customer (contacts) value for a meeting. Reuses AppointmentActivity.contactString —
   *  the same formatter the details pane uses, so multi-contact meetings render as
   *  "First Contact +N" and inactive contacts get the "(Inactive)" suffix.
   *  contactString seeds itself with the global-customer term and only overwrites
   *  it when contacts exist, so it returns "CUSTOMERS" for an empty meeting — guard
   *  on the array length and show N/A (like the Location row) instead. */
  get customer(): string {
    if (!this.isAppointment) return '';
    const appt = this.activity as AppointmentActivity;
    if (!appt.contacts?.length) return this.translate.instant('N_A');
    return appt.contactString || this.translate.instant('N_A');
  }

  /** Reason label — mirrors time-off-details, which uses TIME_OFF_REASON for the
   *  Japanese locale and REASON elsewhere. */
  get reasonLabel(): string {
    return this.localisationService.selectedLanguage?.code === 'ja'
      ? this.translate.instant('TIME_OFF_REASON')
      : this.translate.instant('REASON');
  }

  /** Reason value for a time-off, translated via LocalizationService — the same
   *  path the time-off-details Reason field uses (EntityNameForTranslation.TimeOffReason). */
  get reason(): string {
    if (!this.isTimeOff) return '';
    const raw = (this.activity as TimeOffActivity).reason;
    if (!raw) return this.translate.instant('N_A');
    return this.localisationService.getTranslatedString(raw, EntityNameForTranslation.TimeOffReason) || raw;
  }

  get dateLabel(): string {
    const start = this.activity?.scheduledStart;
    if (!start) return '';
    return new Date(start).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  }

  get timeRange(): string {
    const start = this.activity?.scheduledStart;
    const end = this.activity?.scheduledEnd;
    if (!start || !end) return '';
    const hour12 = this.dateTimeFormatsService.is12HourFormat;
    const fmt = (d: Date) => new Date(d).toLocaleTimeString([], { hour12, hour: '2-digit', minute: '2-digit' });
    return `${fmt(start)} – ${fmt(end)}`;
  }

  get durationLabel(): string {
    const mins = this.activity?.durationInMinutes;
    if (!mins || mins <= 0) return '';
    return `${mins} ${this.translate.instant('RANGE_MIN')}`;
  }

  get iconSrc(): string {
    return this.activity ? this.activityService.getActivityIcon(this.activity) : '';
  }

  get iconMaskStyles(): { [key: string]: string } {
    const mask = `url('${this.iconSrc}') center / contain no-repeat`;
    return { 'mask': mask, '-webkit-mask': mask };
  }

  open(): void {
    this.popoverCtrl.dismiss({ action: 'open' as ActivityChipPopoverResult });
  }

  close(): void {
    this.popoverCtrl.dismiss({ action: 'close' as ActivityChipPopoverResult });
  }
}
